// Catalan is the reference dictionary: es.ts and gl.ts must have the same shape
// (enforced by the `Dict` type and by tests/unit/i18n.test.ts).
// Placeholders use {name} syntax.

export const ca = {
  langName: "Català",
  meta: {
    locale: "ca_ES",
    siteName: "NetSereno PDF",
    homeTitle: "Eines PDF gratuïtes i privades, sense pujar fitxers | NetSereno PDF",
    homeDescription:
      "Uneix, divideix, comprimeix, converteix, signa i protegeix PDF directament al navegador. Gratis, sense registre i sense enviar els teus documents a cap servidor.",
    toolTitleSuffix: "| NetSereno PDF",
    noscript: "Aquesta aplicació necessita JavaScript per processar els PDF al teu dispositiu.",
    chooseLanguage: "Tria l’idioma",
  },
  nav: {
    skip: "Salta al contingut",
    home: "Inici",
    tools: "Eines",
    allTools: "Totes les eines",
    privacy: "Privacitat",
    language: "Idioma",
    themeLight: "Tema clar",
    themeDark: "Tema fosc",
    menu: "Menú",
    resources: "Més recursos",
    netsereno: "Visita NetSereno",
  },
  categories: {
    organize: "Organitzar",
    optimize: "Optimitzar",
    convertTo: "Convertir a PDF",
    convertFrom: "Convertir des de PDF",
    edit: "Editar",
    security: "Seguretat i privacitat",
  },
  home: {
    badge: "100 % al teu dispositiu · sense pujar res",
    heroTitle: "Les eines PDF de sempre, sense enviar els teus documents enlloc",
    heroSubtitle:
      "Uneix, divideix, comprimeix, converteix i signa PDF directament al navegador. Gratis, sense registre i sense límits. Els fitxers no surten mai del teu ordinador.",
    ctaPrimary: "Comença ara",
    ctaSecondary: "Per què és privat?",
    searchLabel: "Cerca una eina",
    searchPlaceholder: "Cerca: unir, comprimir, signar…",
    noResults: "Cap eina coincideix amb la cerca.",
    popular: "Popular",
    trust: [
      { title: "Sense pujades", text: "El processament es fa amb WebAssembly al teu navegador. Cap fitxer viatja per Internet." },
      { title: "Funciona sense connexió", text: "Després de la primera visita, pots desconnectar-te i continuar treballant." },
      { title: "Sense registre ni límits", text: "Ni comptes, ni correus, ni marques d’aigua, ni límits de mida artificials." },
      { title: "En català, castellà i gallec", text: "Una alternativa local i transparent als serveis estrangers en línia." },
    ],
    howTitle: "Com funciona",
    howSteps: [
      { title: "Obre l’eina", text: "El navegador descarrega l’aplicació (com qualsevol web) i la desa per fer-la servir sense connexió." },
      { title: "Afegeix els fitxers", text: "Els documents es llegeixen a la memòria del teu dispositiu. No es pugen a cap servidor." },
      { title: "Descarrega el resultat", text: "El PDF resultant es genera al teu equip. En tancar la pestanya, no en queda cap rastre." },
    ],
    whyTitle: "Per què és important?",
    whyText:
      "Cada dia es pugen milers de documents sensibles (informes mèdics, nòmines, contractes, DNI) a webs gratuïtes allotjades fora de la UE. Encara que prometin esborrar-los, perds el control sobre qui hi accedeix. Amb NetSereno PDF, el document no surt mai del teu dispositiu: és la manera més senzilla de complir el RGPD quan treballes amb PDF.",
    faqTitle: "Preguntes freqüents",
    faq: [
      {
        q: "De debò que els meus fitxers no es pugen?",
        a: "Sí. Tot el codi s’executa al teu navegador. Ho pots comprovar obrint les eines de desenvolupador (pestanya Xarxa) o activant el mode avió: les eines continuen funcionant. A més, la política de seguretat del lloc (CSP) impedeix tècnicament enviar dades a altres servidors.",
      },
      {
        q: "És gratuït? On és el truc?",
        a: "És un recurs gratuït de NetSereno, una empresa de ciberseguretat. No hi ha publicitat ni venda de dades: volem demostrar que la privacitat i la comoditat són compatibles.",
      },
      {
        q: "Hi ha límit de mida o de nombre de fitxers?",
        a: "No hi ha límits artificials. L’únic límit és la memòria del teu dispositiu: en ordinadors normals es poden processar documents de centenars de pàgines.",
      },
      {
        q: "Puc fer-ho servir a la feina sense instal·lar res?",
        a: "Sí. Només cal un navegador modern (Chrome, Edge, Firefox o Safari). També la pots «instal·lar» com a aplicació des del navegador per obrir-la sense connexió.",
      },
      {
        q: "La signatura té validesa legal?",
        a: "L’eina Signar PDF afegeix una signatura manuscrita (imatge). No substitueix una signatura electrònica qualificada amb certificat digital (idCAT, DNIe, FNMT).",
      },
    ],
    ctaTitle: "Els teus PDF ja són privats. I la resta de la teva empresa?",
    ctaText:
      "NetSereno vigila si les dades de la teva organització circulen per la dark web, analitza vulnerabilitats del teu web i entrena el teu equip contra el phishing.",
    ctaButton: "Descobreix NetSereno",
    ctaResources: "Més recursos gratuïts",
    ctaEyebrow: "Ciberseguretat per a organitzacions",
    toolsEyebrow: "Eines gratuïtes i privades",
  },
  common: {
    selectFiles: "Selecciona fitxers",
    selectFile: "Selecciona un fitxer",
    dropHere: "o arrossega’ls aquí",
    dropHereOne: "o arrossega’l aquí",
    dropActive: "Deixa anar els fitxers per afegir-los",
    pasteHint: "També pots enganxar fitxers amb Ctrl+V.",
    accepted: "Formats: {formats}",
    localNotice: "Els fitxers es processen en aquest dispositiu i no es pugen enlloc.",
    addMore: "Afegeix més fitxers",
    remove: "Elimina",
    moveUp: "Mou amunt",
    moveDown: "Mou avall",
    moveLeft: "Mou a l’esquerra",
    moveRight: "Mou a la dreta",
    sortByName: "Ordena per nom",
    startOver: "Torna a començar",
    processing: "Processant…",
    loading: "Carregant…",
    rendering: "Generant miniatures…",
    done: "Fet!",
    download: "Descarrega",
    downloadAll: "Descarrega-ho tot (ZIP)",
    continueWith: "Continua amb una altra eina",
    continueWithLabel: "Fes servir el resultat a…",
    cancel: "Cancel·la",
    close: "Tanca",
    ok: "D’acord",
    options: "Opcions",
    files: "Fitxers",
    pageN: "Pàgina {n}",
    pagesCount: "{n} pàgines",
    pageCountOne: "1 pàgina",
    selectAll: "Selecciona-les totes",
    selectNone: "No en seleccionis cap",
    invertSelection: "Inverteix la selecció",
    selectedCount: "{n} de {total} seleccionades",
    pageRange: "Pàgines",
    pageRangeHint: "Ex.: 1-3, 5, 8- (buit = totes)",
    pageRangeSelectHint: "Fes clic a les miniatures o escriu un interval.",
    allPages: "Totes les pàgines",
    customPages: "Pàgines concretes",
    fileTooBig: "«{name}» ocupa {size}. En dispositius amb poca memòria pot fallar.",
    unsupported: "«{name}» no és un format compatible amb aquesta eina.",
    onlyOne: "Aquesta eina treballa amb un sol fitxer. S’ha fet servir «{name}».",
    minFiles: "Afegeix almenys {n} fitxers per continuar.",
    resultReady: "El resultat està llest",
    resultFiles: "{n} fitxers generats",
    sizeChange: "{before} → {after}",
    reduced: "{percent} més petit",
    elapsed: "Temps: {seconds} s",
    yes: "Sí",
    no: "No",
    color: "Color",
    fontSize: "Mida de la lletra",
    opacity: "Opacitat",
    position: "Posició",
    margin: "Marge",
    none: "Cap",
    small: "Petit",
    medium: "Mitjà",
    large: "Gran",
    reset: "Restableix",
    retry: "Torna-ho a provar",
    readMore: "Més informació",
    new: "Nou",
  },
  positions: {
    topLeft: "A dalt a l’esquerra",
    topCenter: "A dalt al centre",
    topRight: "A dalt a la dreta",
    middleLeft: "Al mig a l’esquerra",
    center: "Al centre",
    middleRight: "Al mig a la dreta",
    bottomLeft: "A baix a l’esquerra",
    bottomCenter: "A baix al centre",
    bottomRight: "A baix a la dreta",
  },
  errors: {
    title: "No s’ha pogut completar",
    generic: "S’ha produït un error inesperat: {message}",
    invalidPdf: "«{name}» no sembla un PDF vàlid o està malmès. Prova l’eina Reparar PDF.",
    invalidImage: "No s’ha pogut llegir la imatge «{name}».",
    wrongPassword: "La contrasenya no és correcta.",
    outOfMemory: "El dispositiu s’ha quedat sense memòria. Prova amb menys pàgines, una resolució més baixa o un altre navegador.",
    engineDownload: "No s’ha pogut descarregar el motor necessari. Comprova la connexió i torna-ho a provar.",
    noText: "Aquest PDF no conté text seleccionable (sembla escanejat). Prova primer l’eina OCR.",
    noPagesSelected: "Selecciona almenys una pàgina.",
    allPagesDeleted: "No pots eliminar totes les pàgines del document.",
    invalidRange: "L’interval «{range}» no és vàlid per a un document de {total} pàgines.",
    encryptedSkipped: "S’ha omès «{name}» perquè està protegit amb contrasenya.",
    noFiles: "Encara no has afegit cap fitxer.",
    browserUnsupported: "El teu navegador no és compatible amb aquesta funció. Fes servir una versió recent de Chrome, Edge, Firefox o Safari.",
  },
  password: {
    title: "PDF protegit amb contrasenya",
    text: "«{name}» està protegit. Escriu la contrasenya per obrir-lo. Es desxifra només en aquest dispositiu.",
    label: "Contrasenya",
    submit: "Obre",
    skip: "Omet aquest fitxer",
    show: "Mostra la contrasenya",
  },
  engines: {
    ocrName: "Motor d’OCR (Tesseract)",
    officeName: "Motor de documents (LibreOffice)",
    gsName: "Motor Ghostscript (compressió i PDF/A)",
    mupdfName: "Motor MuPDF (censura de dades)",
    needDownloadTitle: "Cal descarregar un motor addicional",
    needDownloadText:
      "Aquesta eina fa servir {engine}, que ocupa uns {size}. Es descarrega una sola vegada des d’aquest mateix web i es desa al navegador perquè funcioni sense connexió. Els teus documents continuen sense sortir del dispositiu.",
    download: "Descarrega i continua",
    downloading: "Descarregant {engine}… {percent}",
    starting: "Iniciant {engine}… La primera vegada pot trigar fins a un minut.",
    available: "Disponible sense connexió",
    notDownloaded: "No descarregat",
  },
  offline: {
    online: "En línia",
    offline: "Sense connexió: les eines continuen funcionant",
    offlineShort: "Sense connexió",
    ready: "Llest per funcionar sense connexió",
    updateReady: "Hi ha una versió nova disponible.",
    reload: "Actualitza",
    panelTitle: "Ús sense connexió",
    panelText: "L’aplicació ja es desa al navegador. Pots descarregar ara els motors opcionals per tenir-ho tot disponible sense connexió:",
    prepare: "Descarrega",
    remove: "Esborra",
    unsupported: "Aquest navegador no permet desar l’aplicació per fer-la servir sense connexió.",
    storage: "Espai utilitzat: {used}",
  },
  footer: {
    resourceBy: "Un recurs gratuït de",
    about:
      "NetSereno és un servei de ciberseguretat inspirat en els serenos: vigilem la teva presència digital, detectem filtracions a la dark web i formem equips contra el phishing.",
    toolsTitle: "Eines més utilitzades",
    infoTitle: "Informació",
    privacy: "Com protegim la teva privacitat",
    licenses: "Llicències de codi obert",
    netsereno: "NetSereno.com",
    resources: "Recursos gratuïts de NetSereno",
    source: "Codi font (AGPL)",
    rights: "© {year} NetSereno. Tots els drets reservats.",
    noCookies: "Sense galetes, sense analítica, sense pujades.",
  },
  tools: {
    merge: {
      name: "Unir PDF",
      description: "Combina diversos PDF i imatges en un sol document, en l’ordre que vulguis.",
      metaTitle: "Unir PDF gratis i sense pujar fitxers",
      metaDescription: "Uneix diversos fitxers PDF en un de sol directament al navegador. Ordena’ls arrossegant. Gratis, privat i sense registre.",
      action: "Uneix els PDF",
      hint: "Arrossega els fitxers per canviar-ne l’ordre.",
      blankForDuplex: "Afegeix una pàgina en blanc després dels documents amb pàgines senars (per imprimir a doble cara)",
      suffix: "unit",
    },
    split: {
      name: "Dividir PDF",
      description: "Separa un PDF en diversos fitxers per intervals, cada N pàgines o pàgina a pàgina.",
      metaTitle: "Dividir PDF en diversos fitxers, gratis i privat",
      metaDescription: "Divideix un PDF per intervals de pàgines o en fitxers individuals sense pujar-lo a Internet. Gratis i sense registre.",
      action: "Divideix el PDF",
      mode: "Mode",
      modeRanges: "Per intervals",
      modeEvery: "Cada N pàgines",
      modeAll: "Una pàgina per fitxer",
      ranges: "Intervals (un fitxer per interval)",
      rangesHint: "Ex.: 1-3, 4-6, 7-",
      every: "Pàgines per fitxer",
      suffix: "part",
    },
    extract: {
      name: "Extreure pàgines",
      description: "Crea un PDF nou només amb les pàgines que triïs.",
      metaTitle: "Extreure pàgines d’un PDF gratis i privat",
      metaDescription: "Selecciona visualment les pàgines d’un PDF i desa-les en un document nou. Sense pujar fitxers.",
      action: "Extreu les pàgines",
      hint: "Fes clic a les pàgines que vols conservar.",
      separate: "Desa cada pàgina en un fitxer separat",
      suffix: "extracte",
    },
    delete: {
      name: "Eliminar pàgines",
      description: "Esborra les pàgines que no vulguis d’un PDF.",
      metaTitle: "Eliminar pàgines d’un PDF gratis i privat",
      metaDescription: "Elimina pàgines d’un PDF fent clic a les miniatures. Gratis, sense registre i sense pujar el fitxer.",
      action: "Elimina les pàgines",
      hint: "Fes clic a les pàgines que vols eliminar.",
      suffix: "editat",
    },
    organize: {
      name: "Organitzar PDF",
      description: "Reordena, gira, duplica, elimina i afegeix pàgines amb una vista de miniatures.",
      metaTitle: "Organitzar pàgines d’un PDF: reordenar, girar i eliminar",
      metaDescription: "Arrossega les pàgines per reordenar-les, gira-les, duplica-les o elimina-les. Combina pàgines de diversos PDF. Tot al teu navegador.",
      action: "Desa el PDF",
      hint: "Arrossega les miniatures per reordenar. Fes servir els botons de cada pàgina per girar-la, duplicar-la o eliminar-la.",
      addBlank: "Pàgina en blanc",
      addFiles: "Afegeix fitxers",
      reverse: "Inverteix l’ordre",
      rotateLeft: "Gira a l’esquerra",
      rotateRight: "Gira a la dreta",
      duplicate: "Duplica",
      blankPage: "En blanc",
      suffix: "organitzat",
    },
    rotate: {
      name: "Girar PDF",
      description: "Gira totes les pàgines o només les que triïs.",
      metaTitle: "Girar PDF gratis: rota pàgines sense pujar fitxers",
      metaDescription: "Gira les pàgines d’un PDF 90° o 180°, totes o algunes. Gratis, privat i al teu navegador.",
      action: "Aplica la rotació",
      hint: "Fes servir els botons de cada pàgina o gira-les totes alhora.",
      allLeft: "Gira totes a l’esquerra",
      allRight: "Gira totes a la dreta",
      suffix: "girat",
    },
    compress: {
      name: "Comprimir PDF",
      description: "Redueix la mida dels PDF per enviar-los per correu o pujar-los a portals.",
      metaTitle: "Comprimir PDF gratis sense pujar el fitxer",
      metaDescription: "Redueix el pes dels teus PDF optimitzant imatges i estructura, directament al navegador. Privat, gratis i sense límits.",
      action: "Comprimeix",
      level: "Nivell de compressió",
      lossless: "Sense pèrdua",
      losslessDesc: "Optimitza l’estructura interna sense tocar la qualitat.",
      recommended: "Recomanada",
      recommendedDesc: "Bon equilibri entre mida i qualitat (imatges a uns 150 ppp).",
      strong: "Màxima",
      strongDesc: "La mida més petita (imatges a uns 100 ppp). Les fotos perden qualitat.",
      noGain: "«{name}» ja estava optimitzat: s’ha conservat l’original.",
      grayscale: "Converteix a escala de grisos",
      grayscaleHint: "Treu el color de textos i imatges: ideal per imprimir i redueix encara més la mida.",
      grayForms: "«{name}» té camps de formulari: no s’ha convertit a escala de grisos per no perdre’ls.",
      grayFailed: "No s’ha pogut convertir «{name}» a escala de grisos; s’ha comprimit en color.",
      suffix: "comprimit",
    },
    repair: {
      name: "Reparar PDF",
      description: "Recupera PDF malmesos o que no s’obren correctament.",
      metaTitle: "Reparar PDF malmesos gratis i en privat",
      metaDescription: "Reconstrueix l’estructura de fitxers PDF danyats directament al navegador, sense pujar-los.",
      action: "Repara",
      note: "Reconstrueix la taula d’objectes i l’estructura interna. No pot recuperar contingut que s’hagi perdut del tot.",
      suffix: "reparat",
    },
    ocr: {
      name: "OCR: PDF cercable",
      description: "Reconeix el text de PDF escanejats i imatges perquè es pugui cercar i copiar.",
      metaTitle: "OCR en català, castellà i gallec: PDF escanejat a text",
      metaDescription: "Converteix PDF escanejats i imatges en PDF cercables o en text. Reconeixement òptic de caràcters al teu navegador, sense pujar fitxers.",
      action: "Aplica l’OCR",
      languages: "Idiomes del document",
      output: "Resultat",
      outputPdf: "PDF cercable",
      outputTxt: "Text (.txt)",
      outputBoth: "Tots dos",
      skipText: "Omet les pàgines que ja tenen text",
      resolution: "Qualitat",
      resolutionNormal: "Normal (200 ppp)",
      resolutionHigh: "Alta (300 ppp, més lenta)",
      progress: "Reconeixent la pàgina {page} de {total}…",
      langCa: "Català",
      langEs: "Castellà",
      langGl: "Gallec",
      langEn: "Anglès",
      pickLanguage: "Tria almenys un idioma.",
      suffix: "ocr",
    },
    imagesToPdf: {
      name: "JPG a PDF",
      description: "Converteix fotos i imatges (JPG, PNG, WebP…) en un PDF.",
      metaTitle: "Convertir JPG a PDF gratis i sense pujar fotos",
      metaDescription: "Converteix imatges JPG, PNG o WebP en PDF directament al navegador. Tria la mida de pàgina i l’ordre. Privat i gratuït.",
      action: "Converteix a PDF",
      pageSize: "Mida de pàgina",
      sizeA4: "A4",
      sizeLetter: "Carta",
      sizeFit: "Igual que la imatge",
      orientation: "Orientació",
      orientationAuto: "Automàtica",
      portrait: "Vertical",
      landscape: "Horitzontal",
      separate: "Crea un PDF per cada imatge",
      suffix: "imatges",
    },
    wordToPdf: {
      name: "Word a PDF",
      description: "Converteix documents de Word, LibreOffice Writer o RTF a PDF.",
      metaTitle: "Convertir Word a PDF gratis i sense pujar el document",
      metaDescription: "Converteix DOC, DOCX, ODT i RTF a PDF amb LibreOffice executat al teu navegador. Sense pujar fitxers.",
      action: "Converteix a PDF",
      note: "La conversió la fa LibreOffice dins el teu navegador. Si el document fa servir fonts poc habituals, el resultat pot variar lleugerament.",
    },
    excelToPdf: {
      name: "Excel a PDF",
      description: "Converteix fulls de càlcul d’Excel, LibreOffice Calc o CSV a PDF.",
      metaTitle: "Convertir Excel a PDF gratis i en privat",
      metaDescription: "Converteix XLS, XLSX, ODS i CSV a PDF amb LibreOffice al teu navegador. Sense pujar fitxers.",
      action: "Converteix a PDF",
      note: "Es fa servir l’àrea d’impressió i la configuració de pàgina del full de càlcul.",
    },
    pptToPdf: {
      name: "PowerPoint a PDF",
      description: "Converteix presentacions de PowerPoint o LibreOffice Impress a PDF.",
      metaTitle: "Convertir PowerPoint a PDF gratis i en privat",
      metaDescription: "Converteix PPT, PPTX i ODP a PDF amb LibreOffice al teu navegador. Sense pujar fitxers.",
      action: "Converteix a PDF",
      note: "Cada diapositiva es converteix en una pàgina. Les animacions no es conserven.",
    },
    pdfToImages: {
      name: "PDF a JPG",
      description: "Desa cada pàgina d’un PDF com a imatge JPG o PNG.",
      metaTitle: "Convertir PDF a JPG o PNG gratis i en privat",
      metaDescription: "Converteix les pàgines d’un PDF en imatges JPG o PNG d’alta qualitat directament al navegador.",
      action: "Converteix a imatges",
      format: "Format",
      resolution: "Resolució",
      dpiLow: "72 ppp (pantalla)",
      dpiMedium: "150 ppp (recomanada)",
      dpiHigh: "300 ppp (impressió)",
    },
    pdfToText: {
      name: "PDF a text",
      description: "Extreu tot el text d’un PDF en un fitxer .txt.",
      metaTitle: "Extreure el text d’un PDF gratis i en privat",
      metaDescription: "Copia tot el text d’un PDF en un fitxer de text pla, al teu navegador i sense pujar el document.",
      action: "Extreu el text",
      separators: "Marca l’inici de cada pàgina",
      pageHeader: "--- Pàgina {n} ---",
    },
    pdfToWord: {
      name: "PDF a Word",
      description: "Converteix el text d’un PDF en un document Word (DOCX) editable.",
      metaTitle: "Convertir PDF a Word (DOCX) gratis i en privat",
      metaDescription: "Converteix un PDF en un document DOCX editable amb els paràgrafs originals, sense pujar-lo a Internet.",
      action: "Converteix a Word",
      note: "Es conserven el text, els paràgrafs i els salts de pàgina. Les taules, les imatges i els dissenys complexos no es reprodueixen. Per a PDF escanejats, aplica primer l’OCR.",
    },
    pdfa: {
      name: "PDF a PDF/A",
      description: "Converteix un PDF al format d’arxiu a llarg termini PDF/A, exigit per moltes administracions.",
      metaTitle: "Convertir PDF a PDF/A gratis i sense pujar el fitxer",
      metaDescription: "Converteix PDF a PDF/A-1b, PDF/A-2b o PDF/A-3b per arxivar-los o presentar-los a una seu electrònica, al teu navegador. Privat i gratuït.",
      action: "Converteix a PDF/A",
      level: "Versió de PDF/A",
      pdfa2: "PDF/A-2b (recomanada)",
      pdfa2Desc: "La més acceptada: admet transparències i capes.",
      pdfa1: "PDF/A-1b",
      pdfa1Desc: "Per a sistemes antics que només accepten PDF/A-1.",
      pdfa3: "PDF/A-3b",
      pdfa3Desc: "Com la 2b, però permet adjuntar fitxers de qualsevol tipus.",
      note: "Les fonts s’incrusten, els colors es converteixen a sRGB i s’eliminen els elements que el format no admet (JavaScript, contingut extern…). Els formularis es converteixen en contingut fix. Si necessites acreditar la conformitat, valida el resultat amb veraPDF.",
      removed: "Alguns elements de «{name}» no eren compatibles amb PDF/A i s’han eliminat.",
      suffix: "pdfa",
    },
    sign: {
      name: "Signar PDF",
      description: "Afegeix la teva signatura manuscrita, la data o text a qualsevol pàgina.",
      metaTitle: "Signar PDF gratis: afegeix la teva signatura sense pujar el document",
      metaDescription: "Dibuixa, escriu o puja la teva signatura i col·loca-la al PDF. Tot es fa al teu navegador, sense registre.",
      action: "Desa el PDF signat",
      create: "Crea la signatura",
      edit: "Canvia la signatura",
      tabDraw: "Dibuixa",
      tabType: "Escriu",
      tabUpload: "Imatge",
      drawHint: "Signa amb el ratolí, el dit o un llapis.",
      clear: "Esborra",
      typePlaceholder: "Escriu el teu nom",
      uploadHint: "Puja una foto o un escaneig de la teva signatura.",
      removeBackground: "Fes transparent el fons blanc",
      useSignature: "Fes servir aquesta signatura",
      addSignature: "Afegeix la signatura",
      addDate: "Afegeix la data",
      addText: "Afegeix text",
      textPrompt: "Text a afegir",
      placeHint: "Arrossega els elements per moure’ls i fes servir la cantonada per canviar-ne la mida.",
      applyAll: "Repeteix a totes les pàgines",
      removeItem: "Elimina l’element",
      prevPage: "Pàgina anterior",
      nextPage: "Pàgina següent",
      noItems: "Afegeix almenys una signatura o un text.",
      disclaimer: "És una signatura manuscrita (imatge), no una signatura electrònica qualificada amb certificat digital.",
      suffix: "signat",
    },
    pageNumbers: {
      name: "Numerar pàgines",
      description: "Afegeix números de pàgina amb la posició i el format que vulguis.",
      metaTitle: "Afegir números de pàgina a un PDF gratis",
      metaDescription: "Numera les pàgines d’un PDF triant posició, format i primera pàgina, directament al navegador.",
      action: "Numera les pàgines",
      format: "Format",
      formatN: "1",
      formatNOfTotal: "1 / 10",
      formatPage: "Pàgina 1 de 10",
      pageWord: "Pàgina {n} de {total}",
      startNumber: "Primer número",
      fromPage: "Comença a la pàgina",
      suffix: "numerat",
    },
    watermark: {
      name: "Marca d’aigua",
      description: "Estampa un text o una imatge (CONFIDENCIAL, ESBORRANY, el teu logotip…).",
      metaTitle: "Afegir marca d’aigua a un PDF gratis i en privat",
      metaDescription: "Afegeix una marca d’aigua de text o imatge als teus PDF, amb opacitat i rotació configurables, sense pujar-los.",
      action: "Afegeix la marca d’aigua",
      type: "Tipus",
      typeText: "Text",
      typeImage: "Imatge",
      text: "Text",
      defaultText: "CONFIDENCIAL",
      image: "Imatge (PNG o JPG)",
      chooseImage: "Tria una imatge",
      imageScale: "Mida (% de l’amplada de pàgina)",
      rotation: "Rotació",
      layout: "Distribució",
      layoutCenter: "Una al centre",
      layoutTile: "Mosaic",
      noImage: "Tria una imatge per a la marca d’aigua.",
      suffix: "marca",
    },
    flatten: {
      name: "Aplanar PDF",
      description: "Converteix els formularis emplenats en contingut fix que ja no es pot modificar.",
      metaTitle: "Aplanar formularis PDF gratis i en privat",
      metaDescription: "Aplana els camps de formulari d’un PDF perquè no es puguin editar, directament al navegador.",
      action: "Aplana",
      note: "Els valors dels camps es conserven, però deixen de ser editables.",
      noFields: "«{name}» no té camps de formulari; s’ha desat igualment.",
      suffix: "aplanat",
    },
    metadata: {
      name: "Metadades",
      description: "Consulta, edita o elimina l’autor, el programari i altres dades ocultes.",
      metaTitle: "Veure, editar i eliminar metadades de PDF",
      metaDescription: "Elimina les metadades ocultes d’un PDF (autor, programari, dates) abans de compartir-lo, o edita-les. Sense pujar el fitxer.",
      action: "Desa",
      mode: "Què vols fer?",
      modeEdit: "Editar les metadades",
      modeStrip: "Eliminar-les totes (recomanat abans de compartir)",
      stripNote: "S’eliminaran el títol, l’autor, el programari, les dates, els identificadors i les metadades XMP.",
      title: "Títol",
      author: "Autor",
      subject: "Assumpte",
      keywords: "Paraules clau",
      creator: "Aplicació de creació",
      producer: "Productor",
      creationDate: "Data de creació",
      modDate: "Data de modificació",
      current: "Metadades actuals",
      empty: "(buit)",
      suffix: "metadades",
    },
    protect: {
      name: "Protegir PDF",
      description: "Xifra un PDF amb contrasenya (AES-256) i limita’n la impressió o la còpia.",
      metaTitle: "Protegir PDF amb contrasenya gratis (AES-256)",
      metaDescription: "Posa contrasenya a un PDF amb xifratge AES-256 i tria els permisos, directament al navegador.",
      action: "Protegeix el PDF",
      password: "Contrasenya per obrir",
      confirm: "Repeteix la contrasenya",
      mismatch: "Les contrasenyes no coincideixen.",
      empty: "Escriu una contrasenya.",
      permissions: "Permisos",
      allowPrint: "Permet imprimir",
      allowCopy: "Permet copiar text i imatges",
      allowModify: "Permet modificar el document",
      allowAnnotate: "Permet comentaris i emplenar formularis",
      note: "Xifratge AES-256. Si oblides la contrasenya, no hi ha manera de recuperar-la.",
      suffix: "protegit",
    },
    unlock: {
      name: "Desbloquejar PDF",
      description: "Treu la contrasenya d’un PDF del qual coneixes la contrasenya.",
      metaTitle: "Treure la contrasenya d’un PDF gratis i en privat",
      metaDescription: "Elimina la contrasenya i les restriccions d’un PDF quan en coneixes la clau. Tot es fa al teu navegador.",
      action: "Desbloqueja",
      note: "Necessites conèixer la contrasenya: aquesta eina no trenca contrasenyes. Si el PDF només tenia restriccions d’impressió o còpia, s’eliminen.",
      notEncrypted: "«{name}» no estava protegit; s’ha desat sense canvis.",
      suffix: "desbloquejat",
    },
    redact: {
      name: "Censurar PDF",
      description: "Elimina de debò dades personals o qualsevol zona d’un PDF, i detecta DNI, correus, telèfons i IBAN.",
      metaTitle: "Censurar PDF gratis: elimina dades personals sense pujar el document",
      metaDescription: "Ratlla en negre i elimina de veritat DNI, correus, telèfons, IBAN o qualsevol zona d’un PDF. Es fa al teu navegador, sense pujar el fitxer.",
      action: "Aplica la censura",
      hint: "Arrossega sobre la pàgina per marcar una zona, o fes servir la cerca i la detecció automàtica. Fes clic a la creu d’una zona per desmarcar-la.",
      area: "Zona marcada",
      searchLabel: "Cerca un text",
      searchPlaceholder: "Nom, adreça, número…",
      searchButton: "Marca les coincidències",
      detectTitle: "Detecció automàtica",
      detectId: "DNI i NIE",
      detectEmail: "Adreces de correu",
      detectPhone: "Telèfons",
      detectIban: "Comptes bancaris (IBAN)",
      detectButton: "Detecta i marca",
      found: "S’han marcat {n} coincidències noves.",
      notFound: "No s’ha trobat cap coincidència. Si el PDF és escanejat, marca les zones a mà o aplica-hi abans l’OCR.",
      marked: "Zones marcades: {n}",
      onPage: "{n} en aquesta pàgina",
      clearAll: "Desmarca-ho tot",
      removeMark: "Desmarca la zona",
      prevPage: "Pàgina anterior",
      nextPage: "Pàgina següent",
      stripMetadata: "Elimina també les metadades (autor, títol, programari…)",
      note: "La censura és definitiva: el text, les imatges, els traços, els camps de formulari i els comentaris que queden sota les zones s’eliminen del fitxer, no només es tapen. Revisa totes les pàgines abans de desar.",
      noMarks: "Marca almenys una zona per censurar.",
      leftovers: "Atenció: «{text}» encara apareix al document fora de les zones marcades. Revisa’l abans de compartir-lo.",
      suffix: "censurat",
    },
  },
  pages: {
    privacy: {
      title: "Com protegim la teva privacitat",
      metaDescription: "Explicació tècnica de per què NetSereno PDF no pot veure els teus documents i com pots comprovar-ho tu mateix.",
      intro:
        "NetSereno PDF és una aplicació web que s’executa completament al teu navegador. El nostre servidor només envia els fitxers de l’aplicació (HTML, JavaScript i WebAssembly). Els teus documents no hi arriben mai.",
      sections: [
        {
          title: "Què passa quan obres un fitxer",
          text: "El navegador llegeix el fitxer a la memòria RAM del teu dispositiu. Les biblioteques de processament (pdf-lib, PDF.js, Tesseract, qpdf, Ghostscript, MuPDF i LibreOffice compilades a WebAssembly) treballen sobre aquesta còpia local i generen el resultat també en local. En tancar o recarregar la pestanya, la memòria s’allibera.",
        },
        {
          title: "Què no fem",
          text: "No pugem fitxers, no fem servir galetes, no tenim analítica de visitants, no carreguem scripts ni tipus de lletra de tercers i no demanem cap registre. No desem els teus documents al navegador: només hi desem l’aplicació perquè funcioni sense connexió.",
        },
        {
          title: "Com ho pots comprovar",
          text: "1) Obre les eines de desenvolupador del navegador (F12), ves a la pestanya Xarxa i processa un fitxer: no veuràs cap pujada. 2) Activa el mode avió després de carregar la pàgina: totes les eines continuen funcionant. 3) Revisa la capçalera Content-Security-Policy del lloc: la directiva connect-src 'self' impedeix que el codi enviï dades a cap altre servidor.",
        },
        {
          title: "Registres del servidor",
          text: "Com qualsevol web, el servidor que allotja l’aplicació registra les peticions de fitxers estàtics (adreça IP, pàgina i hora) per motius de seguretat i operació. Aquests registres no contenen mai informació dels teus documents.",
        },
        {
          title: "Codi obert",
          text: "El codi font de l’aplicació és públic i lliure (llicència AGPL-3.0): qualsevol persona pot comprovar que fa el que diu. Es basa en biblioteques de codi obert reconegudes, que pots consultar a la pàgina de llicències.",
        },
      ],
    },
    licenses: {
      title: "Llicències de codi obert",
      metaDescription: "Components de codi obert que fa servir NetSereno PDF i les seves llicències.",
      intro: "NetSereno PDF és possible gràcies a aquests projectes de codi obert. Gràcies a totes les persones que hi contribueixen.",
      component: "Component",
      license: "Llicència",
      usage: "Ús",
      appTitle: "Llicència de NetSereno PDF",
      appText: "NetSereno PDF és programari lliure: el pots fer servir, estudiar, modificar i redistribuir segons els termes de la GNU Affero General Public License, versió 3 o posterior. Inclou Ghostscript i MuPDF, d’Artifex Software, que es distribueixen amb la mateixa llicència.",
      sourceLink: "Consulta el codi font",
    },
    notFound: {
      title: "Pàgina no trobada",
      text: "La pàgina que busques no existeix o s’ha mogut.",
      back: "Torna a l’inici",
    },
  },
};

export type Dict = typeof ca;
